<template>
  <div class="pwa-controller">
    <transition name="pwa-network">
      <div v-if="networkBanner" :class="['pwa-network-banner', `is-${networkBanner}`]" role="status" aria-live="polite">
        <i :class="networkBanner === 'offline' ? 'el-icon-warning-outline' : 'el-icon-circle-check'" aria-hidden="true" />
        <span>{{ $t(`pwa.network.${networkBanner}`) }}</span>
      </div>
    </transition>
    <pwa-install-guide
      :visible="installGuideVisible && !standalone"
      :mode="installMode"
      :can-install-android="Boolean(deferredInstallPrompt)"
      :installing="installing"
      @close="closeInstallGuide"
      @install="installAndroid"
    />
  </div>
</template>

<script>
import { pwaConfig } from "../../config/pwa"
import { applyPwaDocumentState, isStandalone, rememberInstallGuideDismissal, resolveInstallMode, shouldAutoShowInstallGuide } from "../../utils/pwa"
import PWAInstallGuide from "./PWAInstallGuide.vue"

export default {
  name: "PWAController",
  components: { PWAInstallGuide },
  data() {
    return {
      standalone: false,
      installGuideVisible: false,
      deferredInstallPrompt: null,
      installing: false,
      registration: null,
      autoOpenTimer: 0,
      updateTimer: 0,
      displayModeQuery: null,
      online: navigator.onLine !== false,
      networkBanner: navigator.onLine === false ? "offline" : "",
      networkBannerTimer: 0
    }
  },
  computed: {
    installMode() {
      return resolveInstallMode(Boolean(this.deferredInstallPrompt))
    }
  },
  mounted() {
    this.standalone = applyPwaDocumentState()
    window.addEventListener("beforeinstallprompt", this.handleBeforeInstallPrompt)
    window.addEventListener("appinstalled", this.handleAppInstalled)
    window.addEventListener("online", this.handleOnline)
    window.addEventListener("offline", this.handleOffline)
    document.addEventListener("visibilitychange", this.handleVisibilityChange)
    this.displayModeQuery = window.matchMedia && window.matchMedia("(display-mode: standalone)")
    if (this.displayModeQuery) {
      if (this.displayModeQuery.addEventListener) this.displayModeQuery.addEventListener("change", this.handleDisplayModeChange)
      else if (this.displayModeQuery.addListener) this.displayModeQuery.addListener(this.handleDisplayModeChange)
    }
    this.scheduleAutomaticGuide()
    this.registerServiceWorker()
  },
  beforeDestroy() {
    window.removeEventListener("beforeinstallprompt", this.handleBeforeInstallPrompt)
    window.removeEventListener("appinstalled", this.handleAppInstalled)
    window.removeEventListener("online", this.handleOnline)
    window.removeEventListener("offline", this.handleOffline)
    document.removeEventListener("visibilitychange", this.handleVisibilityChange)
    if (this.displayModeQuery) {
      if (this.displayModeQuery.removeEventListener) this.displayModeQuery.removeEventListener("change", this.handleDisplayModeChange)
      else if (this.displayModeQuery.removeListener) this.displayModeQuery.removeListener(this.handleDisplayModeChange)
    }
    window.clearTimeout(this.autoOpenTimer)
    window.clearTimeout(this.networkBannerTimer)
    window.clearInterval(this.updateTimer)
  },
  methods: {
    handleOffline() {
      this.online = false
      window.clearTimeout(this.networkBannerTimer)
      this.networkBanner = "offline"
    },
    handleOnline() {
      const recovered = !this.online
      this.online = true
      window.clearTimeout(this.networkBannerTimer)
      this.networkBanner = recovered ? "restored" : ""
      if (!recovered) return
      window.dispatchEvent(new CustomEvent("starlive:network-restored"))
      this.networkBannerTimer = window.setTimeout(() => { this.networkBanner = "" }, 2400)
    },
    scheduleAutomaticGuide() {
      if (!shouldAutoShowInstallGuide()) return
      this.autoOpenTimer = window.setTimeout(() => {
        if (!this.standalone && document.visibilityState === "visible") this.openInstallGuide()
      }, pwaConfig.installGuide.autoOpenDelayMs)
    },
    openInstallGuide() {
      this.standalone = isStandalone()
      if (this.standalone || this.installMode === "unsupported") return false
      this.installGuideVisible = true
      return true
    },
    closeInstallGuide() {
      this.installGuideVisible = false
      rememberInstallGuideDismissal()
    },
    handleBeforeInstallPrompt(event) {
      event.preventDefault()
      this.deferredInstallPrompt = event
    },
    async installAndroid() {
      if (!this.deferredInstallPrompt || this.installing) return
      this.installing = true
      const prompt = this.deferredInstallPrompt
      try {
        await prompt.prompt()
        const choice = await prompt.userChoice
        if (choice && choice.outcome === "accepted") this.installGuideVisible = false
      } finally {
        this.deferredInstallPrompt = null
        this.installing = false
        rememberInstallGuideDismissal()
      }
    },
    handleAppInstalled() {
      this.deferredInstallPrompt = null
      this.installGuideVisible = false
      this.standalone = applyPwaDocumentState()
    },
    handleDisplayModeChange() {
      this.standalone = applyPwaDocumentState()
      if (this.standalone) this.installGuideVisible = false
    },
    async registerServiceWorker() {
      const localSecureContext = location.hostname === "localhost" || location.hostname === "127.0.0.1"
      if (!pwaConfig.serviceWorker.enabled || !("serviceWorker" in navigator) || !(location.protocol === "https:" || localSecureContext) || process.env.NODE_ENV !== "production") return
      try {
        this.registration = await navigator.serviceWorker.register(pwaConfig.serviceWorker.path, { scope: pwaConfig.serviceWorker.scope, updateViaCache: "none" })
        // Let the browser activate a waiting worker after all old clients close.
        // Never refresh an active live room, upload, checkout, or another tab.
        if (pwaConfig.update.autoCheck) {
          this.updateTimer = window.setInterval(() => this.checkForUpdate(), pwaConfig.update.checkIntervalMs)
        }
      } catch (error) {
        console.warn("StarLive service worker registration failed", error)
      }
    },
    async checkForUpdate() {
      if (!this.registration) return
      try {
        await this.registration.update()
      } catch (error) {
        // A temporary network failure must not interrupt the running app.
      }
    },
    handleVisibilityChange() {
      if (document.visibilityState === "visible" && pwaConfig.update.autoCheck) this.checkForUpdate()
    }
  }
}
</script>

<style scoped>
.pwa-controller{position:absolute;width:0;height:0;overflow:visible}
.pwa-network-banner{position:fixed;z-index:14000;top:calc(max(10px,var(--safe-top)) + 8px);left:50%;display:flex;align-items:center;gap:8px;max-width:min(92vw,430px);min-height:40px;padding:9px 15px;border:1px solid rgba(255,255,255,.14);border-radius:22px;color:#fff;background:rgba(20,22,28,.94);font-size:13px;line-height:1.35;box-shadow:0 12px 34px rgba(0,0,0,.34);transform:translateX(-50%);backdrop-filter:blur(16px);-webkit-backdrop-filter:blur(16px)}
.pwa-network-banner.is-offline i{color:#ffb45d}.pwa-network-banner.is-restored i{color:#20e2d7}
.pwa-network-enter-active,.pwa-network-leave-active{transition:opacity .2s ease,transform .24s ease}.pwa-network-enter,.pwa-network-leave-to{opacity:0;transform:translate(-50%,-8px)}
@media(prefers-reduced-motion:reduce){.pwa-network-enter-active,.pwa-network-leave-active{transition:none}}
</style>
